import { useNavigate } from 'react-router-dom';
import { 
  Mic, 
  Users, 
  Sparkles, 
  BookOpen,
  Heart,
  MessageSquare,
  Camera,
  ChevronRight,
  CheckCircle,
  Building2,
  Home,
  Calendar,
  Sun,
  ClipboardList,
  ArrowRight
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Beta announcement banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-quiltt-purple text-white text-center text-xs font-medium py-2 px-4">
        🎉 Life Story is in <span className="font-bold">Beta</span> — we'd love your feedback!{' '}
        <a href="mailto:support@quiltt.com" className="underline underline-offset-2 hover:text-white/80 transition-colors">
          Let us know what you think →
        </a>
      </div>

      {/* Navigation */}
      <nav className="fixed top-8 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Life Story</span>
            <span className="text-xs font-bold bg-purple-100 text-quiltt-purple px-2 py-0.5 rounded-full">BETA</span>
            <span className="text-xs text-gray-400 ml-1">by Quiltt</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2.5 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Start a Story
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Every life has a story worth{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-quiltt-purple to-purple-600">
                  preserving
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Capture meaningful conversations with seniors through guided questions, 
                let family contribute memories from anywhere, and use AI to deliver 
                truly personalized, resident-centered care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#for-communities"
                  className="flex items-center justify-center gap-3 px-8 py-4 bg-quiltt-purple text-white font-semibold rounded-xl hover:bg-purple-700 transition-all shadow-lg shadow-purple-200"
                >
                  <Building2 className="w-5 h-5" />
                  For Communities
                </a>
                <a
                  href="#for-families"
                  className="flex items-center justify-center gap-3 px-8 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  <Home className="w-5 h-5" />
                  For Families
                </a>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                Free to capture, forever • No credit card required
              </p>
            </div>
            <div className="relative">
              {/* Hero illustration - stylized app preview */}
              <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 shadow-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-quiltt-purple" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Susan's Life Story</p>
                      <p className="text-sm text-gray-500">12 memories captured</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <Mic className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-700">Recording childhood memories...</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Family & Traditions</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Career & Accomplishments</span>
                    </div>
                  </div>
                </div>
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-pink-500" />
                    <span className="text-sm font-medium">3 photos added</span>
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium">Family contributing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Four simple steps to capture and preserve a lifetime of memories
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                icon: Mic,
                title: 'Record Conversations',
                description: 'Use guided questions to spark meaningful conversations. Just press record and talk — we handle the rest.'
              },
              {
                step: '2',
                icon: Users,
                title: 'Family Contributes',
                description: 'Share a link with family members so they can add their own stories, memories, and photos from anywhere.'
              },
              {
                step: '3',
                icon: Sparkles,
                title: 'AI Uncovers Insights',
                description: 'Our AI generates Quiltt Cards, identifies what brings joy and purpose, and recommends personalized activities.'
              },
              {
                step: '4',
                icon: BookOpen,
                title: 'Personalize & Print',
                description: 'Use insights to deliver person-centered care, and transform everything into a keepsake book for family.'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-sm font-bold text-quiltt-purple mb-2">Step {item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed for capturing meaningful life stories
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Mic,
                title: 'Audio Recording',
                description: 'High-quality recording with automatic transcription powered by Google Cloud Speech-to-Text.'
              },
              {
                icon: MessageSquare,
                title: '80+ Guided Questions',
                description: 'Thoughtfully crafted prompts covering childhood, family, career, wisdom, and more.'
              },
              {
                icon: Users,
                title: 'Family Contributions',
                description: 'Share a link so family can add memories and photos from anywhere in the world.'
              },
              {
                icon: Sparkles,
                title: 'AI-Powered Insights',
                description: 'Quiltt Cards, conversation starters, and joy & purpose profiles generated from each life story.'
              },
              {
                icon: Calendar,
                title: 'Activity Matching',
                description: 'Sync your activity calendar and get personalized recommendations for each resident, sorted by day.'
              },
              {
                icon: BookOpen,
                title: 'Printed Books',
                description: 'Beautiful keepsake books from 12 to 48 pages, professionally printed and shipped.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-quiltt-purple" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Spotlight */}
      <section id="for-communities" className="py-20 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full text-amber-700 text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Included with Quiltt Pro
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Personalized care starts with<br />
              knowing who someone{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                really is
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Life Story builds a living profile for each resident that grows richer over time — 
              as family contribute memories, staff capture conversations, and activity data layers in. 
              The result: increasingly precise recommendations that help staff deliver 
              truly personalized, resident-centered care.
            </p>
          </div>

          {/* How it works for communities — visual flow */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 relative">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4">
                <Mic className="w-6 h-6 text-quiltt-purple" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Capture Their Story</h3>
              <p className="text-gray-600 text-sm">
                Staff interviews, family contributions, and everyday interactions all feed into 
                a resident's profile — building a deeper picture of who they are over time.
              </p>
              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-gray-300" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 relative">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">AI Finds Joy & Purpose</h3>
              <p className="text-gray-600 text-sm">
                As the profile grows, our AI continuously refines its understanding of what brings 
                each person joy (music, gardening, baking) and purpose (teaching, mentoring, volunteering).
              </p>
              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-gray-300" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Match to Your Calendar</h3>
              <p className="text-gray-600 text-sm">
                Recommendations get smarter as more data comes in — from family stories to 
                activity attendance. Staff always see the most relevant suggestions for each resident.
              </p>
            </div>
          </div>

          {/* Example card */}
          <div className="mt-12 bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden max-w-2xl mx-auto">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Margaret's Recommended Activities</p>
                <p className="text-xs text-gray-500">Based on her life story — next 7 days</p>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {[
                { icon: '☀️', name: 'Garden Club', match: 'Strong', type: 'Joy', bg: 'bg-amber-50', color: 'text-amber-600', reason: 'Margaret grew up on a farm and talks about her rose garden constantly' },
                { icon: '🎯', name: 'Knitting Circle', match: 'Strong', type: 'Purpose', bg: 'bg-blue-50', color: 'text-blue-600', reason: 'She knit blankets for all 8 grandchildren — creating for others gives her meaning' },
                { icon: '✨', name: 'Hymn Sing-Along', match: 'Strong', type: 'Joy + Purpose', bg: 'bg-purple-50', color: 'text-purple-600', reason: 'Sang in her church choir for 40 years — music and faith are central to her identity' }
              ].map((item, i) => (
                <div key={i} className="px-6 py-4 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <span>{item.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm">{item.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">{item.match}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${item.bg} ${item.color}`}>{item.type}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{item.reason}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-3 bg-gray-50 text-xs text-gray-400 text-center">
              Recommendations evolve as family stories, staff observations, and activity data layer in
            </div>
          </div>

          {/* Community benefits grid */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Sun,
                title: 'Higher Engagement',
                description: 'Recommendations sharpen over time as more data comes in — so programming gets more personal, not less.'
              },
              {
                icon: ClipboardList,
                title: 'Quiltt Cards for Staff',
                description: 'One-page summaries help every staff member — even new hires — connect with residents personally from day one.'
              },
              {
                icon: Users,
                title: 'Family Connection',
                description: 'Family members contribute stories and photos remotely, strengthening bonds and enriching the resident\'s profile.'
              },
              {
                icon: BookOpen,
                title: 'Keepsake Books',
                description: 'Transform each life story into a printed book — a meaningful gift that families treasure.'
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-quiltt-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-quiltt-purple" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <a
              href="https://www.quiltt.com/#free-trial"
              className="inline-flex items-center gap-2 px-8 py-4 bg-quiltt-purple text-white font-semibold rounded-xl hover:bg-purple-700 transition-all shadow-lg shadow-purple-200"
            >
              Start Your Free Quiltt Trial
              <ChevronRight className="w-5 h-5" />
            </a>
            <p className="mt-3 text-sm text-gray-500">Life Story is included with every Quiltt Pro subscription</p>
          </div>
        </div>
      </section>

      {/* For Families — compact card */}
      <section id="for-families" className="py-16 px-4 bg-gray-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Home className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">For Families Too</h3>
                <p className="text-gray-600 mb-4">
                  You don't need to be part of a community to use Life Story. Capture your loved 
                  one's memories through guided conversations, invite family to contribute from anywhere, 
                  and create a beautiful keepsake book — all for free.
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  {[
                    'Capture stories during visits',
                    'Distant relatives can contribute',
                    'Create a legacy for future generations',
                    'Beautiful printed books from $29'
                  ].map((item, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 border-2 border-quiltt-purple text-quiltt-purple font-semibold rounded-xl hover:bg-purple-50 transition-colors flex-shrink-0"
              >
                Start a Story
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Capture stories for free. Only pay when you're ready to print.
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Free tier */}
              <div className="p-8 border-b md:border-b-0 md:border-r border-gray-100">
                <div className="text-sm font-bold text-green-600 mb-2">FREE FOREVER</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">For Families</h3>
                <p className="text-sm text-gray-500 mb-4">Capture and preserve a loved one's story</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900">$0</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Unlimited stories',
                    'Audio recording & transcription',
                    'All 80+ guided questions',
                    'Family contribution links',
                    'AI-generated summaries'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 border-2 border-quiltt-purple text-quiltt-purple font-semibold rounded-xl hover:bg-purple-50 transition-colors"
                >
                  Start a Story — Free
                </button>
              </div>
              
              {/* Quiltt Pro */}
              <div className="p-8 bg-gradient-to-br from-purple-50 to-pink-50">
                <div className="text-sm font-bold text-quiltt-purple mb-2">QUILTT PRO</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">For Communities</h3>
                <p className="text-sm text-gray-500 mb-4">Everything families get, plus</p>
                <div className="mb-6">
                  <span className="text-lg font-medium text-gray-600">Included with your</span><br />
                  <span className="text-3xl font-bold text-gray-900">Quiltt Pro plan</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Resident management dashboard',
                    'Quiltt Cards & conversation starters',
                    'Joy & purpose profiles',
                    'Activity calendar integration',
                    'Personalized activity recommendations',
                    'Staff management & invites'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-quiltt-purple flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://www.quiltt.com/#free-trial"
                  className="block w-full py-3 bg-quiltt-purple text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors text-center"
                >
                  Start Your Free Trial
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-quiltt-purple to-purple-600 rounded-3xl p-12 text-center text-white">
            <h2 className="text-4xl font-bold mb-4">
              Every resident deserves to be truly known
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Whether you're an activity director looking to personalize programming or a 
              family preserving a loved one's legacy — start capturing the stories that matter most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.quiltt.com/#free-trial"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-quiltt-purple font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
              >
                Start a Quiltt Trial
                <ChevronRight className="w-5 h-5" />
              </a>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                Start a Family Story — Free
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Life Story</span>
              <span className="text-xs font-bold bg-purple-100 text-quiltt-purple px-2 py-0.5 rounded-full">BETA</span>
              <span className="text-xs text-gray-400 ml-1">by Quiltt</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <a href="https://quiltt.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900">
                About Quiltt
              </a>
              <a href="mailto:support@quiltt.com" className="hover:text-gray-900">
                Contact
              </a>
              <a href="https://quiltt.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900">
                Privacy
              </a>
            </div>
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Alpine Media Tech
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
